import { Flex, Select, Spin, Typography } from "antd"
import type { BaseOptionType } from "antd/lib/select"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import type {
  ShareEditorEntry,
  ShareEditorKind
} from "../helpers/reportSharing.ts"
import {
  makeRoleEditorEntry,
  makeUserEditorEntry
} from "../helpers/reportSharing.ts"
import {
  filterShareTargetOption,
  useReportShareTargets,
  type ShareTargetOption
} from "../hooks/useReportShareUsers.ts"
import type { ReportOwner } from "../types/types.ts"

const { Text } = Typography

interface ReportSharePeoplePickerProps {
  reportId: string
  owner: ReportOwner
  entries: ShareEditorEntry[]
  kinds: readonly ShareEditorKind[]
  onAdd: (entry: ShareEditorEntry) => void
  className?: string
}

export const ReportSharePeoplePicker = ({
  reportId,
  owner,
  entries,
  kinds,
  onAdd,
  className
}: ReportSharePeoplePickerProps) => {
  const { t } = useTranslation()
  const { options, loading } = useReportShareTargets(
    reportId,
    owner,
    entries,
    kinds
  )
  const [pickerKey, setPickerKey] = useState(0)

  const handleSelect = (_value: string, option: BaseOptionType) => {
    const target = option as ShareTargetOption
    onAdd(
      target.kind === "role"
        ? makeRoleEditorEntry(target.targetId, target.label)
        : makeUserEditorEntry(target.targetId, target.label)
    )
    setPickerKey((key) => key + 1)
  }

  return (
    <Select<string>
      key={pickerKey}
      showSearch
      className={className}
      placeholder={t("reports.share.addPlaceholder")}
      loading={loading}
      options={options}
      filterOption={filterShareTargetOption}
      onSelect={handleSelect}
      aria-label={t("reports.share.addPlaceholder")}
      notFoundContent={
        loading ? <Spin size="small" /> : t("reports.share.noTeamMembers")
      }
      optionRender={(option) => {
        const data = option.data as ShareTargetOption
        return (
          <Flex vertical className="min-w-0">
            <Text strong ellipsis>
              {data.label}
            </Text>
            {data.kind === "role" ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("reports.share.role")}
              </Text>
            ) : null}
            {data.email ? (
              <Text
                type="secondary"
                style={{ fontSize: 12 }}
                ellipsis={{ tooltip: data.email }}
              >
                {data.email}
              </Text>
            ) : null}
          </Flex>
        )
      }}
    />
  )
}
